import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter, useSearch } from '@tanstack/react-router';
import { adminViews, directory, isForbidden, users } from '@cwos/api-client';
import type { LocationVerification } from '@cwos/ui-shared';
import {
  Avatar,
  ErrorPanel,
  FilterSummary,
  PausedStrip,
  Pill,
  Refused,
  SkeletonSet,
  SuccessEmpty,
  TableCard,
  VerificationBadge,
  useTabListKeys,
  type Column,
} from '@cwos/ui-admin';
import { useAuth } from '../../lib/auth.js';
import { ADMIN_VIEWS_READ, mayListWorkers, usePermissions } from '../permissions.js';
import { authorityGate, gateOpen, type AuthorityGate } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { useNav } from '../navigation.js';
import { ExportCsvButton } from '../reporting/ExportCsvButton.js';
import { SettingsField } from '../settings/common.js';
import {
  EMPTY_CLIENT_DRAFT,
  EMPTY_WORKER_DRAFT,
  canSubmitClient,
  canSubmitWorker,
  clientCreatePayload,
  clientEditDraftFrom,
  clientEditIsEmpty,
  clientEditProblem,
  clientUpdatePayload,
  type ClientDraft,
  type ClientEditDraft,
  type WorkerDraft,
  workerCreatePayload,
} from './directoryLogic.js';
import {
  boardNarrowedBy,
  clockedInLabel,
  durationLabel,
  emptyBoardCopy,
  isKnownVerification,
  normalizeView,
  type BoardView,
} from './board.js';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import { LoadFailure } from '../LoadFailure.js';
import { useConnectorLookup } from '../visits/provenance.js';
import { clientOriginSentence } from './directoryLogic.js';

type ActiveRow = adminViews.ActiveSessionEntry;
type WorkerRow = directory.Worker;
type ClientRow = directory.Client;

/**
 * Team & caseloads — live board + directory (doc 01 A3.1). "Working now" is the
 * default; toggle to "Everyone" for the worker roster or "Clients" for the
 * client roster. Live board polls every 60s; a failed poll shows a PausedStrip
 * and keeps the data on screen (never a map — location is per-session, behind a
 * reason to look, and recorded).
 *
 * "Everyone" and "Clients" are also where a brand-new, empty organization adds
 * its first roster entries: a directory `worker` row is a distinct concept from
 * a login (an office administrator may hold no worker row; a worker may hold no
 * login — see `directory/service.ts`), so creating one lives here rather than
 * on the People & roles (invitations) screen.
 */
export function TeamBoard() {
  const { me } = useAuth();
  // The whole state, not just `has`. `has` is the DOOR — it gates the three
  // `enabled:` flags below and stays permissive on purpose. The FilterSummary's
  // completeness promise is the other question, and `boardNarrowedBy` needs the
  // scope-aware `can` plus the two loading flags to answer it without guessing.
  const permissions = usePermissions();
  const { has } = permissions;
  const canListWorkers = mayListWorkers(permissions);
  /**
   * Withheld, and not merely unanswered.
   *
   * `canListWorkers` is default-deny, which is right for `enabled:` and wrong for
   * the sentence "The roster is not shown to you": while the permission set is
   * loading, and after it fails to load, it is `false` for a reader who may hold
   * both keys. Only a RESOLVED set licenses telling somebody what their own
   * account cannot do.
   */
  const rosterWithheld = !permissions.isLoading && !permissions.isError && !canListWorkers;
  const navigate = useNav();
  const router = useRouter();
  const search = useSearch({ strict: false }) as { view?: string };
  const view: BoardView = normalizeView(search.view);
  const viewTabs = useTabListKeys<HTMLDivElement>();

  const setView = (next: BoardView) => {
    void router.navigate({ to: '/people', search: next === 'working' ? {} : { view: next } });
  };

  const board = useQuery({
    queryKey: ['admin-views', 'workforce'],
    queryFn: () => adminViews.workforce(),
    refetchInterval: 60_000,
    // `admin-views:read`, and the tab state is not a substitute for it. Every
    // `/api/admin-views/*` route is guarded by that key alone
    // (admin-views/routes.ts:133), and 1755220000000_admin-views-permission
    // withholds it from `worker` on purpose. `/people` is an ungated route, so
    // this fired for anybody who clicked the tab — and it shares the
    // `['admin-views','workforce']` key with the Time clock screens, so the
    // 403 was cached there too.
    enabled: view === 'working' && has(ADMIN_VIEWS_READ),
  });

  const roster = useQuery({
    queryKey: ['directory', 'workers'],
    queryFn: () => directory.workers(),
    // `user:read` as well as `directory:read` — the roster is the list of the
    // users in the organization and the server refuses it outright without that
    // key, rather than narrowing it. Firing anyway would put a 403 in the
    // `['directory','workers']` cache that Scheduling, Timesheets, Approvals and
    // both messaging pickers then read for the rest of the session.
    //
    // `mayListWorkers` asks `can()` at organization scope, matching both of the
    // route's gates: `guard(..., 'directory:read')` and `mayReadWorkerIdentity`
    // for `user:read` are each `can()` with no scope argument, so a grant
    // confined to one program is a 403 here and not a shorter roster.
    enabled: view === 'everyone' && canListWorkers,
  });

  const clientRoster = useQuery({
    queryKey: ['directory', 'clients'],
    queryFn: () => directory.clients(),
    // `GET /directory/clients` requires `directory:read` (directory/routes.ts:586)
    // and REFUSES without it rather than narrowing — the same reason the roster
    // beside it checks its keys. This asked only which tab was open, so on an
    // ungated route it put a 403 into `['directory','clients']`, which the
    // worker profile's client list and the global search both read afterwards.
    enabled: view === 'clients' && has('directory:read'),
  });

  const scope = {
    what:
      view === 'working'
        ? 'people working now'
        : view === 'clients'
          ? 'clients in your directory'
          : 'everyone on the team',
    scope: me ? (me.orgName ?? me.orgId) : 'your organization',
    period: view === 'working' ? 'right now' : 'all staff',
    narrowings: [],
    // The mandatory summary used to print "That's everything — no hidden
    // filters" here unconditionally. On a caseload board and on a roster this
    // reader may not hold the key for, that is the strongest false statement on
    // the screen: it promises completeness in the component whose job is to be
    // believed about it.
    // `boardNarrowedBy` takes the READER, not a boolean, so the not-yet-known
    // case is answered inside the function rather than guessed at here. The
    // sibling change that landed alongside this one worked around the missing
    // third state at this call site and said in its own report that the
    // parameter should become a population — this is that, so the workaround is
    // dropped rather than kept beside its own replacement.
    narrowedBy: boardNarrowedBy(view, board.data, permissions) ?? undefined,
  };

  return (
    <div>
      <h1 className="rn-card-title" style={{ marginBottom: 'var(--space-12)' }}>
        Team &amp; caseloads
      </h1>

      {/* `aria-pressed` dropped (invalid on `role="tab"`), ids and a panel
          added, arrow keys supplied by the hook. */}
      <div className="rn-tabs" role="tablist" aria-label="Team view" {...viewTabs}>
        <button
          type="button"
          className="rn-tab"
          role="tab"
          id="team-tab-working"
          aria-controls="team-panel"
          aria-selected={view === 'working'}
          onClick={() => setView('working')}
        >
          Working now
        </button>
        <button
          type="button"
          className="rn-tab"
          role="tab"
          id="team-tab-everyone"
          aria-controls="team-panel"
          aria-selected={view === 'everyone'}
          onClick={() => setView('everyone')}
        >
          Everyone
        </button>
        <button
          type="button"
          className="rn-tab"
          role="tab"
          id="team-tab-clients"
          aria-controls="team-panel"
          aria-selected={view === 'clients'}
          onClick={() => setView('clients')}
        >
          Clients
        </button>
      </div>

      <FilterSummary scope={scope} />
      {view === 'working' ? (
        <ExportCsvButton
          fallbackFilename="cwos-workforce.csv"
          label="Export board CSV"
          onExport={() => adminViews.exportWorkforceCsv()}
        />
      ) : null}
      {view !== 'clients' ? (
        <p className="rn-muted" style={{ marginBottom: 'var(--space-12)' }}>
          No map here — location is per-session, behind a reason to look, and recorded.
        </p>
      ) : null}

      {/* Opening a worker goes to `/people/:workerId`. It previously pointed at
          `/people/w/:id`, a route nothing ever registered, so clicking a person
          in the directory silently did nothing. */}
      <div id="team-panel" role="tabpanel" aria-labelledby={`team-tab-${view}`}>
        {view === 'working' ? (
          <LiveBoard
            query={board}
            gate={authorityGate(permissions, ADMIN_VIEWS_READ)}
            onRetry={permissions.refetch}
            onOpenSession={(id) => navigate(`/sessions/${id}`)}
          />
        ) : view === 'everyone' ? (
          <Directory
            query={roster}
            rosterWithheld={rosterWithheld}
            onOpenWorker={(id) => navigate(`/people/${id}`)}
          />
        ) : (
          <Clients
            query={clientRoster}
            gate={authorityGate(permissions, 'directory:read')}
            onRetry={permissions.refetch}
          />
        )}
      </div>
    </div>
  );
}

function LiveBoard({
  query,
  gate,
  onRetry,
  onOpenSession,
}: {
  query: ReturnType<typeof useQuery<adminViews.WorkforceBoard>>;
  /**
   * The reader's answer for `admin-views:read`. A GATE and not a boolean: this
   * took a `mayRead` prop derived straight from the default-deny predicate, and
   * `/people` is an UNGATED route (`App.tsx` registers it with no
   * `RequirePermission` above it), so on every cold load — before `/auth/me`
   * had answered — this panel told
   * the reader the board was not theirs to see. `Directory` beside it already
   * claimed its refusal only from a resolved set (`rosterWithheld`); this panel
   * and `Clients` did not.
   */
  gate: AuthorityGate;
  onRetry: () => void;
  onOpenSession: (sessionId: string) => void;
}) {
  // A disabled query has no data, no error and is not loading, which `listPhase`
  // reads as `loading` — correctly, since nothing was asked. But a spinner that
  // never resolves is its own lie, so the reason it was never asked is stated
  // here, ahead of the phase, exactly as `Directory` does for the roster.
  if (!gateOpen(gate)) {
    return (
      <GateNotice
        gate={gate}
        what="see the live board"
        onRetry={onRetry}
        refusedNode={
          <Refused
            heading="This board is not yours to see"
            message="The live board is the organization's timekeeping, and reading it is a separate permission from reading your own. It is not empty — nobody is being left off it by mistake."
          />
        }
      />
    );
  }

  const data = query.data;
  // An organization with no time clock has nobody who COULD be on it: "All
  // quiet — nobody is clocked in" would be a measurement of a source it never
  // connected (owner ruling 2026-10-07).
  if (data?.hasTimeClock === false) {
    return (
      <p className="rn-muted">
        No time clock connected — this organization does not record clock-ins in Phibian, so nobody
        is shown as working now. Delivered visits are on the Visits screen.
      </p>
    );
  }
  const phase = listPhase(query, data === undefined ? undefined : data.activeSessions.length);
  const emptyCopy = emptyBoardCopy(data);

  const columns: Column<adminViews.ActiveSessionEntry>[] = [
    {
      key: 'name',
      header: 'Person',
      /* A board of people that shows only names is a board that reads as rows.
         The avatar is decorative — the name is right beside it in text, so it
         is never the only identity signal — but it is what turns a table of
         strings back into a team. */
      render: (r) => (
        <span className="tb-person">
          <Avatar name={r.displayName} size={28} />
          <span style={{ fontWeight: 600 }}>{r.displayName}</span>
        </span>
      ),
    },
    {
      key: 'since',
      header: 'On the clock',
      render: (r) => durationLabel(r.durationMinutes),
      numeric: true,
    },
    {
      key: 'checkin',
      header: 'Check-in',
      render: (r) =>
        isKnownVerification(r.locationStatus) ? (
          <VerificationBadge verification={r.locationStatus as LocationVerification} />
        ) : (
          <Pill variant="plain" dot>
            On the clock
          </Pill>
        ),
    },
  ];

  return (
    <ListState
      phase={phase}
      surface="table"
      rows={6}
      failedMessage="We couldn’t load the live board just now. Treat this as unknown, not as nobody working."
      onRetry={() => void query.refetch()}
      refused={
        <Refused
          heading="This board is not yours to see"
          message="Your account does not have access to the live board. It is not empty — nobody is being left off it by mistake."
        />
      }
      empty={
        <div>
          {/* The empty state carries the scope too. `clockedInLabel` names the
              population on the populated path; a caseload reader whose team
              happens to be idle must not be told the organization is. */}
          {query.isError ? <PausedStrip asOf="" onRetry={() => void query.refetch()} /> : null}
          <SuccessEmpty heading={emptyCopy.heading} message={emptyCopy.message} />
        </div>
      }
    >
      {data !== undefined ? (
        <div>
          {query.isError ? <PausedStrip asOf="" onRetry={() => void query.refetch()} /> : null}
          <p className="rn-muted" style={{ margin: '0 0 var(--space-8)' }}>
            {clockedInLabel(data)}
          </p>
          <TableCard
            columns={columns}
            rows={data.activeSessions}
            rowKey={(r: ActiveRow) => r.sessionId}
            onRowOpen={(r: ActiveRow) => onOpenSession(r.sessionId)}
            caption="Every row opens the session behind it."
          />
        </div>
      ) : null}
    </ListState>
  );
}

function Directory({
  query,
  onOpenWorker,
  rosterWithheld,
}: {
  query: ReturnType<typeof useQuery<WorkerRow[]>>;
  onOpenWorker: (workerId: string) => void;
  /**
   * A RESOLVED permission set says the roster's two keys are not held over the
   * organization. Decided once, on the board, rather than re-derived here: the
   * board is where the same answer decides whether the query fires, and two
   * statements of one rule are two things that can drift.
   */
  rosterWithheld: boolean;
}) {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const canManage = has('directory:manage');

  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<WorkerDraft>(EMPTY_WORKER_DRAFT);
  const [createError, setCreateError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => directory.createWorker(workerCreatePayload(draft)),
    onSuccess: () => {
      setCreating(false);
      setDraft(EMPTY_WORKER_DRAFT);
      setCreateError(null);
      void qc.invalidateQueries({ queryKey: ['directory', 'workers'] });
    },
    onError: (err: unknown) => {
      setCreateError(err instanceof Error ? err.message : 'We couldn’t add that worker.');
    },
  });

  const rows = query.data ?? [];

  return (
    <div>
      {canManage ? (
        <div className="rn-card" style={{ marginBottom: 'var(--space-12)' }}>
          <div className="rn-card-head">
            <h2 className="rn-card-title">Roster</h2>
            <button
              type="button"
              className="rn-btn-quiet"
              onClick={() => {
                setCreating((c) => !c);
                setCreateError(null);
              }}
              aria-expanded={creating}
            >
              {creating ? 'Cancel' : 'Add worker'}
            </button>
          </div>
          {creating ? (
            <form
              className="org-create"
              onSubmit={(e) => {
                e.preventDefault();
                if (canSubmitWorker(draft)) create.mutate();
              }}
            >
              <div className="org-create-grid">
                <SettingsField label="Name">
                  <input
                    style={{ marginBottom: 0 }}
                    value={draft.displayName}
                    autoFocus
                    onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
                  />
                </SettingsField>
                <SettingsField
                  label="Role (optional)"
                  hint="A title, not a permission — access still comes from Settings → People & roles."
                >
                  <input
                    style={{ marginBottom: 0 }}
                    value={draft.roleTitle}
                    onChange={(e) => setDraft({ ...draft, roleTitle: e.target.value })}
                  />
                </SettingsField>
              </div>
              {createError ? (
                <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }} role="alert">
                  {createError}
                </p>
              ) : null}
              <div className="set-actions">
                <button
                  type="submit"
                  className="signin-devbtn"
                  style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                  disabled={!canSubmitWorker(draft) || create.isPending}
                >
                  {create.isPending ? 'Adding…' : 'Add worker'}
                </button>
                <button
                  type="button"
                  className="rn-btn-quiet"
                  onClick={() => {
                    setCreating(false);
                    setDraft(EMPTY_WORKER_DRAFT);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : null}
        </div>
      ) : null}

      <DirectoryTable
        query={query}
        rows={rows}
        onOpenWorker={onOpenWorker}
        canManage={canManage}
        withheld={rosterWithheld}
      />
    </div>
  );
}

/**
 * The roster table, and the reason it needed `withheld`.
 *
 * `GET /directory/workers` needs `user:read` as well as `directory:read`, so
 * the query is DISABLED for a reader without it — and react-query reports a
 * disabled query as neither loading nor errored with `data === undefined`. That
 * fell through `query.data ?? []` into `rows.length === 0` and told the reader
 * "No staff yet — nobody has been added to the roster yet."
 *
 * A person who may not be told who works here was being told that nobody does.
 * That is the sentence the withholding contract opens with, made about the
 * whole organization, on the screen called Team.
 *
 * The flag is `withheld` and not `mayList` because those are not complements.
 * "May list" is false for three reasons — unheld, held only over a program, and
 * not yet known — and only the first two are a fact about the account. The
 * third is a fact about the request for the permission set, and the caller
 * resolves it before this renders; the query is still disabled in that case, so
 * `listPhase` reports `loading`, which is what it is.
 */
function DirectoryTable({
  query,
  rows,
  onOpenWorker,
  canManage,
  withheld,
}: {
  query: ReturnType<typeof useQuery<WorkerRow[]>>;
  rows: WorkerRow[];
  onOpenWorker: (workerId: string) => void;
  canManage: boolean;
  /**
   * True only when a RESOLVED permission set says the reader does not hold the
   * roster's two keys over the organization. Never true merely because the set
   * has not arrived.
   */
  withheld: boolean;
}) {
  if (withheld) {
    return (
      <Refused
        heading="The roster is not shown to you"
        message="Naming the people in your organization needs the directory permission. This is not an empty roster — it is one you have not been given."
      />
    );
  }

  const phase = listPhase(query, query.data === undefined ? undefined : rows.length);

  const columns: Column<WorkerRow>[] = [
    {
      key: 'name',
      header: 'Person',
      // Photo only when the worker's LOGIN has one (photoUpdatedAt); initials
      // are the honest fallback for everyone else, never a placeholder image.
      render: (r) => (
        <span className="tb-person">
          <Avatar
            name={r.displayName}
            size={26}
            src={r.userId && r.photoUpdatedAt ? users.photoUrl(r.userId, r.photoUpdatedAt) : null}
          />{' '}
          <span style={{ fontWeight: 600 }}>{r.displayName}</span>
        </span>
      ),
    },
    { key: 'role', header: 'Role', render: (r) => r.roleTitle ?? '—' },
    {
      key: 'hours',
      header: 'Expected / wk',
      render: (r) => (r.expectedWeeklyHours !== null ? `${r.expectedWeeklyHours}h` : '—'),
      numeric: true,
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <Pill variant={r.employmentStatus === 'active' ? 'good' : 'plain'} dot>
          {r.employmentStatus}
        </Pill>
      ),
    },
  ];

  return (
    <ListState
      phase={phase}
      surface="table"
      rows={6}
      failedMessage="We couldn’t load the directory just now. This is not a roster of nobody."
      onRetry={() => void query.refetch()}
      empty={
        <SuccessEmpty
          heading="No staff yet"
          message={
            canManage
              ? 'A worker is a roster entry — the record that lets someone show up on schedules, caseloads and time tracking. Add the first one above. It is separate from inviting a login: give someone sign-in access from Settings → People & roles.'
              : 'Nobody has been added to the roster yet. An administrator with directory:manage adds workers here.'
          }
        />
      }
    >
      <TableCard
        columns={columns}
        rows={rows}
        rowKey={(r: WorkerRow) => r.id}
        onRowOpen={(r: WorkerRow) => onOpenWorker(r.id)}
      />
    </ListState>
  );
}

/**
 * Client roster. Every read is the initials-only projection; a "Reveal name"
 * action calls the distinct, audited `client:reveal_name` endpoint and holds
 * the returned name only in this component's own state — never in the
 * react-query cache, never logged, and gone as soon as the view is left.
 */
function Clients({
  query,
  gate,
  onRetry,
}: {
  query: ReturnType<typeof useQuery<ClientRow[]>>;
  /**
   * The reader's answer for `directory:read` — a gate, for the reason
   * `LiveBoard` gives above. `/people` is ungated, so the boolean version said
   * "the client list is not shown to you" to everybody, on every first paint.
   */
  gate: AuthorityGate;
  onRetry: () => void;
}) {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const canManage = has('directory:manage');
  const canReveal = has('client:reveal_name');
  // PH-UX-002: resolve each row's installation id to a connector name. The
  // read is gated on the reader's own key; without it every row says "the
  // EHR", which is exactly what is known.
  const connectors = useConnectorLookup(has('integrations:read'));

  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<ClientDraft>(EMPTY_CLIENT_DRAFT);
  const [createError, setCreateError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [revealingId, setRevealingId] = useState<string | null>(null);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => directory.createClient(clientCreatePayload(draft)),
    onSuccess: () => {
      setCreating(false);
      setDraft(EMPTY_CLIENT_DRAFT);
      setCreateError(null);
      void qc.invalidateQueries({ queryKey: ['directory', 'clients'] });
    },
    onError: (err: unknown) => {
      setCreateError(err instanceof Error ? err.message : 'We couldn’t add that client.');
    },
  });

  const reveal = useMutation({
    mutationFn: (clientId: string) => directory.revealName(clientId),
    onMutate: (clientId) => {
      setRevealingId(clientId);
      setRevealError(null);
    },
    onSuccess: (result) => {
      setRevealed((prev) => ({ ...prev, [result.id]: `${result.firstName} ${result.lastName}` }));
    },
    onError: (err: unknown) => {
      setRevealError(err instanceof Error ? err.message : 'We couldn’t reveal that name.');
    },
    onSettled: () => setRevealingId(null),
  });

  // MOVED BELOW THE HOOKS, and that is a fix rather than a tidy-up. This
  // component returned `<Refused>` from ABOVE its six `useState` calls, so the
  // moment `directory:read` resolved from absent to held — which is every sign-in
  // where the permission set arrives after the first paint — React saw a
  // different number of hooks on the second render and threw, taking the screen
  // down to the error boundary. The refusal is the same sentence, rendered from
  // a position that cannot reorder anything.
  if (!gateOpen(gate)) {
    return (
      <GateNotice
        gate={gate}
        what="see the client list"
        onRetry={onRetry}
        refusedNode={
          <Refused
            heading="The client list is not shown to you"
            message="Reading who your organization serves needs the directory permission. This is not an empty list — it is one you have not been given."
          />
        }
      />
    );
  }

  const rows = query.data ?? [];

  const columns: Column<ClientRow>[] = [
    {
      key: 'name',
      header: 'Client',
      render: (r) => (
        <span style={{ fontWeight: 600 }}>
          {revealed[r.id] ?? r.initials}
          {canReveal && !revealed[r.id] ? (
            <button
              type="button"
              className="rn-link"
              style={{ marginLeft: 'var(--space-8)', fontWeight: 400 }}
              disabled={revealingId === r.id}
              onClick={(e) => {
                e.stopPropagation();
                reveal.mutate(r.id);
              }}
            >
              {revealingId === r.id ? 'Revealing…' : 'Reveal name'}
            </button>
          ) : null}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <Pill variant={r.status === 'active' ? 'good' : 'plain'} dot>
          {r.status}
        </Pill>
      ),
    },
    {
      key: 'ref',
      header: 'External ref',
      render: (r) => r.externalRef ?? '—',
    },
    {
      key: 'origin',
      header: 'Origin',
      render: (r) => (
        <span className="rn-muted">
          {clientOriginSentence(r, connectors, (iso) => new Date(iso).toLocaleString())}
        </span>
      ),
    },
  ];

  if (canManage) {
    columns.push({
      key: 'edit',
      header: 'Record',
      render: (r) => (
        <button
          type="button"
          className="rn-link"
          aria-expanded={editingId === r.id}
          onClick={(e) => {
            e.stopPropagation();
            setEditingId((id) => (id === r.id ? null : r.id));
          }}
        >
          {editingId === r.id ? 'Close' : 'Edit'}
        </button>
      ),
    });
  }

  return (
    <div>
      {canManage ? (
        <div className="rn-card" style={{ marginBottom: 'var(--space-12)' }}>
          <div className="rn-card-head">
            <h2 className="rn-card-title">Clients</h2>
            <button
              type="button"
              className="rn-btn-quiet"
              onClick={() => {
                setCreating((c) => !c);
                setCreateError(null);
              }}
              aria-expanded={creating}
            >
              {creating ? 'Cancel' : 'Add client'}
            </button>
          </div>
          {creating ? (
            <form
              className="org-create"
              onSubmit={(e) => {
                e.preventDefault();
                if (canSubmitClient(draft)) create.mutate();
              }}
            >
              <p className="rn-muted" style={{ fontSize: 13, marginTop: 0 }}>
                The name is stored, but this screen — and every other list — shows only initials.
                Revealing a full name later is a separate, recorded action.
              </p>
              <div className="org-create-grid">
                <SettingsField label="First name">
                  <input
                    style={{ marginBottom: 0 }}
                    value={draft.firstName}
                    autoFocus
                    onChange={(e) => setDraft({ ...draft, firstName: e.target.value })}
                  />
                </SettingsField>
                <SettingsField label="Last name">
                  <input
                    style={{ marginBottom: 0 }}
                    value={draft.lastName}
                    onChange={(e) => setDraft({ ...draft, lastName: e.target.value })}
                  />
                </SettingsField>
              </div>
              {createError ? (
                <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }} role="alert">
                  {createError}
                </p>
              ) : null}
              <div className="set-actions">
                <button
                  type="submit"
                  className="signin-devbtn"
                  style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                  disabled={!canSubmitClient(draft) || create.isPending}
                >
                  {create.isPending ? 'Adding…' : 'Add client'}
                </button>
                <button
                  type="button"
                  className="rn-btn-quiet"
                  onClick={() => {
                    setCreating(false);
                    setDraft(EMPTY_CLIENT_DRAFT);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : null}
        </div>
      ) : null}

      {revealError ? (
        <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }} role="alert">
          {revealError}
        </p>
      ) : null}

      {query.isLoading ? (
        <SkeletonSet surface="table" rows={6} />
      ) : query.isError && !query.data ? (
        <LoadFailure
          error={query.error}
          message="We couldn’t load clients just now."
          onRetry={() => void query.refetch()}
        />
      ) : rows.length === 0 ? (
        <SuccessEmpty
          heading="No clients yet"
          message={
            canManage
              ? 'A client record is what a worker gets assigned to and what services and time get attributed to. It is identified by initials everywhere except a deliberate, audited reveal. Add the first one above.'
              : 'Nobody has been added to the client directory yet. An administrator with directory:manage adds clients here.'
          }
        />
      ) : (
        <TableCard columns={columns} rows={rows} rowKey={(r: ClientRow) => r.id} />
      )}

      {editingId && canManage ? (
        <ClientEditor clientId={editingId} onClose={() => setEditingId(null)} />
      ) : null}
    </div>
  );
}

/**
 * EDIT ONE CLIENT RECORD.
 *
 * `PATCH /directory/clients/:id` shipped with the directory and had no caller,
 * so a client's details were write-once: a name typed wrong at intake stayed
 * wrong, and a discharge could only be recorded by discharging the record
 * outright.
 *
 * IT READS THROUGH THE READ PATH. `GET /directory/clients/:id` (`directory.client`)
 * is what seeds this form. An empty-bodied PATCH would also return the whole
 * record — a sibling lane found exactly that — and it would be a write in the
 * audit log, a write in every permission check, and a `client.updated` event for
 * a person who only opened a form. A read spelled as a write is a write.
 *
 * WHAT IT SHOWS is bounded by what that read returns. The roster row beside it
 * carries no `dob` at all (`ClientListView = Omit<ClientView, 'dob'>`), which is
 * why the form is seeded from the single read and not from the row that was
 * clicked. The stored NAME is not returned by any read path — only the audited
 * reveal — so those two fields start blank and say what blank means, rather than
 * presenting an empty box that reads as "this client has no name".
 */
function ClientEditor({ clientId, onClose }: { clientId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<ClientEditDraft | null>(null);
  const [saveError, setSaveError] = useState<{ refused: boolean; message: string } | null>(null);
  const [saved, setSaved] = useState(false);

  const record = useQuery({
    queryKey: ['directory', 'client', clientId],
    queryFn: () => directory.client(clientId),
  });

  // Seeded once per record, from the record. `draft` stays null until the read
  // lands, so there is never a moment where an empty form is showing values the
  // API has not yet supplied.
  const current = record.data;
  const working = draft ?? (current ? clientEditDraftFrom(current) : null);

  const save = useMutation({
    mutationFn: () => {
      if (!current || !working) throw new Error('nothing to save');
      return directory.updateClient(clientId, clientUpdatePayload(working, current));
    },
    onMutate: () => {
      setSaveError(null);
      setSaved(false);
    },
    onSuccess: () => {
      setSaved(true);
      setDraft(null);
      void qc.invalidateQueries({ queryKey: ['directory', 'clients'] });
      void qc.invalidateQueries({ queryKey: ['directory', 'client', clientId] });
      // The palette indexes the roster separately and would otherwise keep
      // serving the old initials.
      void qc.invalidateQueries({ queryKey: ['search', 'clients'] });
    },
    onError: (err: unknown) => {
      // A 403 is an answer: `directory:manage` is what this route asks for and
      // asking again cannot supply it. Everything else failed and can be retried.
      if (isForbidden(err)) {
        setSaveError({
          refused: true,
          message:
            'Your account cannot change client records. Editing one needs directory:manage — this is a decision about your access, not a failure to save.',
        });
        return;
      }
      setSaveError({
        refused: false,
        message: 'We couldn’t save that just now. The record is unchanged.',
      });
    },
  });

  const problem = working ? clientEditProblem(working) : null;
  const patch = working && current ? clientUpdatePayload(working, current) : {};
  const nothingChanged = clientEditIsEmpty(patch);

  return (
    <div className="rn-card" style={{ marginTop: 'var(--space-12)' }}>
      <div className="rn-card-head">
        <h2 className="rn-card-title">Edit this client’s record</h2>
        <button type="button" className="rn-btn-quiet" onClick={onClose}>
          Close
        </button>
      </div>

      {record.isLoading ? (
        <SkeletonSet surface="drawer" rows={4} />
      ) : isForbidden(record.error) ? (
        <Refused
          heading="This record is not shown to you"
          message="Reading one client's details needs the directory permission. Nothing is missing from the record — it is withheld from you."
        />
      ) : record.isError && !record.data ? (
        // NOT an empty form. A failed read here would otherwise render every
        // field blank, and a blank date of birth on a form with a Save button is
        // an offer to erase one.
        <LoadFailure
          error={record.error}
          message="We couldn’t read this client’s record, so there is nothing to edit yet. The fields are not blank — they are unread."
          onRetry={() => void record.refetch()}
        />
      ) : working && current ? (
        <form
          className="org-create"
          onSubmit={(e) => {
            e.preventDefault();
            if (!problem && !nothingChanged) save.mutate();
          }}
        >
          <p className="rn-muted" style={{ fontSize: 13, marginTop: 0 }}>
            Only the fields you change are sent. Emptying a field that has a value clears it;
            leaving a field alone leaves it alone.
          </p>

          <div className="org-create-grid">
            <SettingsField
              label="First name"
              hint="Blank leaves the stored name unchanged — no screen can show you what it is without an audited reveal."
            >
              <input
                style={{ marginBottom: 0 }}
                value={working.firstName}
                onChange={(e) => setDraft({ ...working, firstName: e.target.value })}
              />
            </SettingsField>
            <SettingsField label="Last name" hint="Blank leaves the stored name unchanged.">
              <input
                style={{ marginBottom: 0 }}
                value={working.lastName}
                onChange={(e) => setDraft({ ...working, lastName: e.target.value })}
              />
            </SettingsField>

            <SettingsField label="Date of birth">
              <input
                type="date"

                style={{ marginBottom: 0 }}
                value={working.dob}
                onChange={(e) => setDraft({ ...working, dob: e.target.value })}
              />
            </SettingsField>
            <SettingsField label="External reference" hint="The id this client has in your EHR.">
              <input
                style={{ marginBottom: 0 }}
                value={working.externalRef}
                onChange={(e) => setDraft({ ...working, externalRef: e.target.value })}
              />
            </SettingsField>

            <SettingsField label="Status">
              <select
                style={{ marginBottom: 0 }}
                value={working.status}
                onChange={(e) =>
                  setDraft({ ...working, status: e.target.value as ClientEditDraft['status'] })
                }
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="discharged">Discharged</option>
              </select>
            </SettingsField>
            <SettingsField
              label="Test record"
              hint="Marks this client as a test fixture rather than a person receiving services."
            >
              <select
                style={{ marginBottom: 0 }}
                value={working.isTest ? 'yes' : 'no'}
                onChange={(e) => setDraft({ ...working, isTest: e.target.value === 'yes' })}
              >
                <option value="no">A real client</option>
                <option value="yes">A test record</option>
              </select>
            </SettingsField>

            <SettingsField label="Enrolled on">
              <input
                type="date"

                style={{ marginBottom: 0 }}
                value={working.enrolledOn}
                onChange={(e) => setDraft({ ...working, enrolledOn: e.target.value })}
              />
            </SettingsField>
            <SettingsField label="Discharged on">
              <input
                type="date"

                style={{ marginBottom: 0 }}
                value={working.dischargedOn}
                onChange={(e) => setDraft({ ...working, dischargedOn: e.target.value })}
              />
            </SettingsField>
          </div>

          {/* The program is not on this form on purpose — see
              `clientUpdatePayload` for why a program picker cannot be built
              honestly off a read that narrows silently. */}
          <p className="rn-muted" style={{ fontSize: 13 }}>
            A client’s program is not changed here. Moving somebody between programs is an enrolment
            decision rather than a correction to their record.
          </p>

          {problem ? (
            <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }} role="alert">
              {problem}
            </p>
          ) : null}

          {saveError ? (
            saveError.refused ? (
              <Refused heading="Not yours to change" message={saveError.message} />
            ) : (
              <ErrorPanel message={saveError.message} onRetry={() => save.mutate()} />
            )
          ) : null}

          {saved && nothingChanged ? (
            <p className="rn-muted" role="status" style={{ fontSize: 13 }}>
              Saved.
            </p>
          ) : null}

          <div className="set-actions">
            <button
              type="submit"
              className="signin-devbtn"
              style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
              disabled={Boolean(problem) || nothingChanged || save.isPending}
            >
              {save.isPending ? 'Saving…' : nothingChanged ? 'Nothing changed' : 'Save changes'}
            </button>
            <button type="button" className="rn-btn-quiet" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
